import type { ReactNode } from 'react';
import { useState } from 'react';
import { Link } from 'react-router';
import { Button } from './Button.js';
import { Modal } from './Modal.js';

export interface RowAction {
  key: string;
  label: string;
  href?: string; // navigation → <Link> (plain <a>)
  onClick?: () => void | Promise<void>; // operation → <button>
  variant?: 'default' | 'danger';
  confirmMessage?: string; // if set, click opens the confirm Modal
  // H7: an action the row may not run right now. Rendered non-interactively, carrying the
  // reason, because a missing button explains nothing to the operator — the whole value of
  // refusing Publish is saying that the car has no rate card. Both props are optional, so
  // every existing consumer is unchanged.
  disabled?: boolean;
  disabledReason?: string;
  // 113: an icon-button rendering, replacing the visible text label with this icon while the
  // label stays the element's aria-label. Optional so a consumer without a suitable icon yet
  // keeps today's text-link rendering rather than hitting a type error.
  icon?: ReactNode;
}

const linkClass = (variant: RowAction['variant']) =>
  variant === 'danger' ? 'text-red-600 underline' : 'text-primary underline';

const iconClass = (variant: RowAction['variant']) =>
  variant === 'danger'
    ? 'p-2 -m-2 text-steel hover:text-red-600'
    : 'p-2 -m-2 text-steel hover:text-ink';

/**
 * Shared Actions cell for admin list rows. Renders each action as a plain
 * <Link> (href) or <button> (onClick) inside a `flex gap-3 text-xs` wrapper so
 * DataTable's mobile tap-target hook applies. An action with `confirmMessage`
 * routes through an internal confirm Modal; the action's async `onClick` is
 * awaited so the Modal shows progress, stays open + surfaces the error on
 * failure, and closes on success. State is local per instance.
 */
export function RowActions({ actions }: { actions: Array<RowAction | false | null | undefined> }) {
  const [pending, setPending] = useState<RowAction | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const visible = actions.filter((a): a is RowAction => Boolean(a));

  function close() {
    if (isRunning) return;
    setPending(null);
    setError(null);
  }

  async function run(action: RowAction) {
    setIsRunning(true);
    setError(null);
    try {
      await action.onClick?.();
      setIsRunning(false);
      setPending(null);
    } catch (e) {
      setError((e as { message?: string })?.message ?? 'Action failed');
      setIsRunning(false);
    }
  }

  function handleClick(action: RowAction) {
    if (action.confirmMessage) {
      setError(null);
      setPending(action);
    } else {
      void action.onClick?.();
    }
  }

  return (
    <>
      <div className="flex gap-3 text-xs">
        {visible.map((action) =>
          action.disabled ? (
            // Not a <button disabled>: nothing here is clickable, and the reason must reach
            // both a hovering operator (title) and a screen reader (the sr-only sentence).
            <span key={action.key} className="cursor-not-allowed">
              <span aria-disabled="true" title={action.disabledReason} className="text-steel">
                {action.label}
              </span>
              {action.disabledReason && <span className="sr-only">{action.disabledReason}</span>}
            </span>
          ) : action.href ? (
            <Link
              key={action.key}
              to={action.href}
              aria-label={action.icon ? action.label : undefined}
              className={action.icon ? iconClass(action.variant) : linkClass(action.variant)}
            >
              {action.icon ?? action.label}
            </Link>
          ) : (
            <button
              key={action.key}
              type="button"
              aria-label={action.icon ? action.label : undefined}
              className={action.icon ? iconClass(action.variant) : linkClass(action.variant)}
              onClick={() => handleClick(action)}
            >
              {action.icon ?? action.label}
            </button>
          ),
        )}
      </div>

      <Modal open={pending !== null} onClose={close} title="Please confirm">
        <div className="space-y-4">
          <p className="text-sm text-slate">{pending?.confirmMessage}</p>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3">
            <Button type="button" variant="ghost" onClick={close} disabled={isRunning}>
              Cancel
            </Button>
            <Button
              type="button"
              variant={pending?.variant === 'danger' ? 'danger' : 'primary'}
              loading={isRunning}
              onClick={() => pending && run(pending)}
            >
              Confirm
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
